import {
    ButtonSecondary,
    Colors,
    Icon,
    Label,
    Pills,
    Tabs,
    Text,
    TextInput,
} from '@amelio/ui-library';

import {
    COMPENSATION_CAPTION,
    COMPENSATION_OVERLINE,
    COMPENSATION_TILE_SURFACE,
    COMPENSATION_TILE_VALUE,
} from '../compensationStyles';
import { parseTypedNumber, useTypedNumber } from '../hooks/useTypedNumber';

import type { CompensationBudgetInputMode, CompensationComputedView } from '@/types/compensation';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { formatCurrency, formatPercent } from '@/utils/numberFormat';

const BUDGET_INPUT_ID = 'compensation-budget-amount';

/** The mode label names a group of tabs rather than a field, so the group points back at it with
 *  `aria-labelledby` instead of the label pointing forward with `htmlFor`. */
const BUDGET_MODE_LABEL_ID = 'compensation-budget-mode-label';

interface BudgetCardProps {
    budgetAmount: number;
    budgetInputMode: CompensationBudgetInputMode;
    computed: CompensationComputedView | undefined;
    canEdit: boolean;
    onBudgetChange: (amount: number) => void;
    onBudgetInputModeChange: (mode: CompensationBudgetInputMode) => void;
    /** Absent for a reader who may not record salaries. */
    onImportSalaries?: () => void;
}

/**
 * Section 2, left card: how much money the envelope holds. A fixed amount or a share of the
 * payroll — the second is only a way of typing the first — plus the payroll it is measured
 * against and the shortcut to the salary import that makes that payroll real.
 */
export const BudgetCard = ({
    budgetAmount,
    budgetInputMode,
    computed,
    canEdit,
    onBudgetChange,
    onBudgetInputModeChange,
    onImportSalaries,
}: BudgetCardProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const payroll = computed?.payroll ?? 0;
    const isAmountMode = budgetInputMode === 'Amount';
    // In percent mode the dollars stay what is stored: the percentage is the server's reading of
    // them against the payroll, so it moves when the payroll does.
    const externalValue = isAmountMode
        ? String(budgetAmount)
        : (computed?.averagePercent ?? 0).toFixed(2);
    const amountLabelKey = isAmountMode
        ? 'compensation.budgetCard.amountLabel'
        : 'compensation.budgetCard.percentLabel';
    const modeLabelKey = isAmountMode
        ? 'compensation.budgetCard.modeAmount'
        : 'compensation.budgetCard.modePercent';

    const [inputValue, typeInputValue] = useTypedNumber(externalValue);

    /** The dollars the text just typed resolves to; anything unreadable is no budget at all rather
     * than a stale one. */
    const resolveAmount = (raw: string): number => {
        const typed = parseTypedNumber(raw) ?? 0;
        return isAmountMode ? typed : Math.round((payroll * typed) / 100);
    };

    const handleChange = (raw: string) => {
        const amount = resolveAmount(raw);

        typeInputValue({
            raw,
            // A percentage is only rewritten once the server has read the new amount back.
            nextValueText: isAmountMode ? String(amount) : externalValue,
        });
        onBudgetChange(amount);
    };

    return (
        <div
            className={'flex flex-col gap-[16px]'}
            data-testid={'compensation-budget-card'}
        >
            <div
                role={'group'}
                aria-labelledby={BUDGET_MODE_LABEL_ID}
                className={'flex flex-col gap-1'}
            >
                <Label
                    id={BUDGET_MODE_LABEL_ID}
                    color={Colors.BASE_500}
                >
                    {t('compensation.budgetCard.modeLabel')}
                </Label>

                {/* A closed envelope refuses the change the tabs would dispatch, so it is told
                    which mode is in force instead of being offered two tabs that raise the save
                    bar and then fail on a conflict. `self-start` on both, so neither the segmented
                    group nor the pill stretches to the column and leaves a grey tail. */}
                {canEdit ? (
                    <Tabs
                        variant={'segmented'}
                        className={'self-start'}
                        activeKey={budgetInputMode}
                        onChange={(key) =>
                            onBudgetInputModeChange(key as CompensationBudgetInputMode)
                        }
                        items={[
                            {
                                key: 'Amount',
                                label: t('compensation.budgetCard.modeAmount'),
                                dataTestId: 'compensation-budget-mode-amount',
                            },
                            {
                                key: 'PayrollPercent',
                                label: t('compensation.budgetCard.modePercent'),
                                dataTestId: 'compensation-budget-mode-percent',
                            },
                        ]}
                        dataTestId={'compensation-budget-mode'}
                    />
                ) : (
                    <Pills
                        size={'sm'}
                        className={'self-start'}
                        dataTestId={'compensation-budget-mode-static'}
                    >
                        {t(modeLabelKey)}
                    </Pills>
                )}
            </div>

            <div className={'flex flex-col gap-1'}>
                <Label
                    htmlFor={BUDGET_INPUT_ID}
                    color={Colors.BASE_500}
                >
                    {t(amountLabelKey)}
                </Label>

                <div className={'flex items-center gap-1.5'}>
                    {/* The one figure the whole section hangs off, so it is written like one: navy
                        and bold rather than the library's default grey input text. */}
                    <div className={'flex-1'}>
                        <TextInput
                            id={BUDGET_INPUT_ID}
                            type={'number'}
                            size={'lg'}
                            textSize={'lg'}
                            className={'text-navy font-bold'}
                            value={inputValue}
                            onChange={handleChange}
                            readOnly={!canEdit}
                            dataTestId={'compensation-budget-input'}
                        />
                    </div>

                    {/* Decorative: the label above already names the unit. */}
                    <span
                        aria-hidden
                        className={`${COMPENSATION_CAPTION} text-base-500`}
                    >
                        {isAmountMode ? '$' : '%'}
                    </span>
                </div>

                {isAmountMode ? null : (
                    <Text
                        color={Colors.BASE_500}
                        className={COMPENSATION_CAPTION}
                    >
                        {t('compensation.budgetCard.percentHelp')}
                    </Text>
                )}
            </div>

            <div className={`${COMPENSATION_TILE_SURFACE} flex flex-col gap-1`}>
                <Text
                    color={Colors.BASE_500}
                    className={COMPENSATION_OVERLINE}
                >
                    {t('compensation.budgetCard.payrollLabel')}
                </Text>

                <Text
                    color={Colors.NAVY}
                    className={COMPENSATION_TILE_VALUE}
                >
                    {formatCurrency(payroll, language)}
                </Text>

                <Text
                    color={Colors.BASE_500}
                    className={COMPENSATION_CAPTION}
                >
                    {t('compensation.budgetCard.headcountSummary', {
                        count: computed?.includedCount ?? 0,
                        ratio: formatPercent(computed?.averagePercent ?? 0, language, {
                            fractionDigits: 2,
                        }),
                    })}
                </Text>
            </div>

            {canEdit && onImportSalaries ? (
                <ButtonSecondary
                    type={'button'}
                    size={'sm'}
                    className={'w-full'}
                    onClick={onImportSalaries}
                    leftIcon={
                        <Icon
                            name={'arrow-import'}
                            size={'sm'}
                            ariaHidden
                        />
                    }
                    dataTestId={'compensation-import-salaries-button'}
                >
                    {t('compensation.budgetCard.importSalaries')}
                </ButtonSecondary>
            ) : null}
        </div>
    );
};
